import { Controller } from '@hotwired/stimulus';

import {
  MESSAGES,
  lotNumberKey,
  nextCustomLotId,
  validateCustomLot,
} from '../utils/custom_lot_rules';

/**
 * Custom Lot Controller (« + Ajouter un lot personnalisé »)
 *
 * Ajoute au sélecteur de lots d'un marché un lot propre à ce marché, « NN - Intitulé »,
 * hors de la liste de l'agence. Monté sur la même racine que `multi-select`
 * (`Form::TradesMultiSelectComponent` avec `allow_custom: true`, formulaire marché et
 * modales marché de l'onboarding). Les règles vivent dans `utils/custom_lot_rules.js`.
 *
 * Targets:
 * - group: conteneur « Lots propres à ce marché », masqué par l'attribut `hidden` s'il est vide
 * - list: où ajouter les options clonées
 * - optionTemplate: `<template>` d'une option perso (case sans id ni name)
 * - openButton: bouton « Ajouter un lot personnalisé »
 * - form: ligne de saisie inline (attribut `hidden` au repos)
 * - number / label: champs « N° de lot » / « Intitulé »
 * - error: `<p aria-live="assertive">` des erreurs de saisie
 * - status: zone `.sr-only aria-live="polite"` des annonces (ajout, lot déjà présent coché)
 *
 * Values:
 * - inputName: nom réel des cases (`contract[trades][]` ou `input_name` en mode brut)
 * - idPrefix: préfixe des ids, les cases perso reçoivent `${idPrefix}_custom_${n}`
 *
 * Actions:
 * - open: affiche la ligne de saisie, focus sur le numéro
 * - submit: valide et ajoute (Entrée confinée : ne soumet JAMAIS le formulaire hôte)
 * - cancel: referme la ligne (Échap confiné : ne ferme PAS la modale d'onboarding)
 * - clearError: efface l'erreur quand l'utilisateur corrige le champ fautif
 *
 * Events:
 * - custom-lot:added (bubbles), detail `{ value, checkbox }` : écouté par `multi-select`
 *
 * Sécurité : la saisie n'est posée que par `textContent` / `setAttribute`, jamais `innerHTML`.
 *
 * @example
 * <div data-controller="multi-select custom-lot"
 *      data-multi-select-allow-custom-value="true"
 *      data-custom-lot-input-name-value="contract[trades][]"
 *      data-custom-lot-id-prefix-value="contract_trades">
 *   <div data-custom-lot-target="group" hidden>
 *     <div data-custom-lot-target="list"></div>
 *   </div>
 *   <template data-custom-lot-target="optionTemplate">…</template>
 *   <button type="button" data-custom-lot-target="openButton" data-action="custom-lot#open">
 *     Ajouter un lot personnalisé
 *   </button>
 *   <div data-custom-lot-target="form" hidden>
 *     <input data-custom-lot-target="number"
 *            data-action="keydown.enter->custom-lot#submit keydown.esc->custom-lot#cancel input->custom-lot#clearError">
 *     <input data-custom-lot-target="label" data-action="…">
 *     <p data-custom-lot-target="error" aria-live="assertive"></p>
 *   </div>
 *   <span class="sr-only" aria-live="polite" data-custom-lot-target="status"></span>
 * </div>
 */
export default class extends Controller {
  static targets = [
    'group',
    'list',
    'optionTemplate',
    'openButton',
    'form',
    'number',
    'label',
    'error',
    'notice',
    'status',
  ];

  static values = {
    inputName: String,
    idPrefix: String,
  };

  open(event) {
    event?.preventDefault();
    if (this.hasOpenButtonTarget) this.openButtonTarget.hidden = true;
    this.formTarget.hidden = false;
    this.numberTarget.focus();
  }

  submit(event) {
    // Entrée dans un champ : ne jamais soumettre le formulaire marché ni remonter plus haut.
    event?.preventDefault();
    event?.stopPropagation();
    if (event?.isComposing) return;

    this.#clearInvalid();
    const checkboxes = this.#checkboxes();
    const result = validateCustomLot({
      number: this.numberTarget.value,
      label: this.labelTarget.value,
      checkedValues: checkboxes.filter((c) => c.checked).map((c) => c.value),
      catalogValues: checkboxes.filter((c) => !this.#isCustom(c)).map((c) => c.value),
      existingValues: checkboxes.filter((c) => this.#isCustom(c)).map((c) => c.value),
    });

    if (!result.ok) {
      this.#showError(result.field, result.message);
    } else if (result.catalogMatch) {
      this.#checkExistingLot(result.catalogMatch, MESSAGES.catalogChecked);
    } else if (result.existingMatch) {
      // Lot déjà propre au marché (perso ou orphelin) : une 2e case au même numéro serait refusée.
      this.#checkExistingLot(result.existingMatch, MESSAGES.existingChecked);
    } else {
      this.#addCustomLot(result.value);
    }
  }

  cancel(event) {
    // Échap : referme la ligne sans fermer la modale d'onboarding qui l'héberge.
    event?.preventDefault();
    event?.stopPropagation();

    this.#resetFields();
    this.formTarget.hidden = true;
    if (this.hasOpenButtonTarget) {
      this.openButtonTarget.hidden = false;
      this.openButtonTarget.focus();
    }
  }

  clearError(event) {
    this.#showNotice('');
    const field = event?.target;
    if (field && field.getAttribute('aria-invalid') !== 'true') return;

    this.errorTarget.textContent = '';
    this.#clearInvalid();
  }

  // Coche le lot déjà présent (agence : S7, ou propre au marché) et prévient `multi-select` via `change`.
  #checkExistingLot(value, message) {
    const checkbox = this.#checkboxes().find((c) => c.value === value);
    if (checkbox && !checkbox.checked) {
      checkbox.checked = true;
      checkbox.dispatchEvent(new Event('change', { bubbles: true }));
    }
    this.#resetFields();
    // Seul message visible hors erreur (décision 5) : le lot n'a pas été ajouté mais coché.
    this.#showNotice(message);
    this.numberTarget.focus();
  }

  #addCustomLot(lot) {
    const option = this.#buildOption(lot);
    this.listTarget.appendChild(option);
    this.groupTarget.hidden = false;

    const checkbox = option.querySelector('input[type="checkbox"]');
    this.dispatch('added', { detail: { value: lot, checkbox } });

    this.#resetFields();
    this.#announce(MESSAGES.lotAdded(lot));
    // La ligne reste ouverte pour enchaîner plusieurs lots.
    this.numberTarget.focus();
  }

  #buildOption(lot) {
    const option = this.optionTemplateTarget.content.firstElementChild.cloneNode(true);
    const id = nextCustomLotId(
      this.idPrefixValue,
      this.listTarget.querySelectorAll('input[type="checkbox"]').length,
      (candidate) => document.getElementById(candidate) !== null
    );

    const checkbox = option.querySelector('input[type="checkbox"]');
    checkbox.id = id;
    checkbox.setAttribute('name', this.inputNameValue);
    checkbox.setAttribute('value', lot);
    checkbox.checked = true;

    const label = option.querySelector('label');
    if (label) label.htmlFor = id;

    const text = option.querySelector('[data-custom-lot-part="text"]');
    if (text) text.textContent = lot;

    const offCatalog = option.querySelector('[data-custom-lot-part="offcat"]');
    if (offCatalog) {
      offCatalog.id = `${id}_offcat`;
      checkbox.setAttribute('aria-describedby', offCatalog.id);
    }

    // Badge « n° NN déjà pris » : caché, basculé par `multi-select` via `hidden` + aria-describedby.
    const taken = option.querySelector('[data-multi-select-part="taken"]');
    if (taken) {
      taken.id = `${id}_taken`;
      const takenText = taken.querySelector('[data-custom-lot-part="taken-text"]') ?? taken;
      takenText.textContent = MESSAGES.numberTakenBadge(lotNumberKey(lot));
    }

    return option;
  }

  #showError(field, message) {
    const input = field === 'label' ? this.labelTarget : this.numberTarget;
    this.errorTarget.textContent = message;
    input.setAttribute('aria-invalid', 'true');
    input.focus();
  }

  #resetFields() {
    this.numberTarget.value = '';
    this.labelTarget.value = '';
    this.errorTarget.textContent = '';
    this.#showNotice('');
    this.#clearInvalid();
  }

  #showNotice(message) {
    if (this.hasNoticeTarget) this.noticeTarget.textContent = message;
  }

  #clearInvalid() {
    this.numberTarget.removeAttribute('aria-invalid');
    this.labelTarget.removeAttribute('aria-invalid');
  }

  #announce(message) {
    if (this.hasStatusTarget) this.statusTarget.textContent = message;
  }

  // Cases du sélecteur (le contenu du `<template>` n'est pas dans l'arbre, donc exclu d'office).
  #checkboxes() {
    return Array.from(this.element.querySelectorAll('input[type="checkbox"]'));
  }

  #isCustom(checkbox) {
    return checkbox.dataset.orphan === 'true';
  }
}
